import { useEffect, useState, Fragment } from "react";
import { CssVarsProvider, useColorScheme } from "@mui/joy/styles";
import GlobalStyles from "@mui/joy/GlobalStyles";
import CssBaseline from "@mui/joy/CssBaseline";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Select, { selectClasses } from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import IconButton, { IconButtonProps } from "@mui/joy/IconButton";
import Link from "@mui/joy/Link";
import Input from "@mui/joy/Input";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import Textarea from "@mui/joy/Textarea";
import logo from "../assets/logo.svg";
import darkLogo from "../assets/darkLogo.svg";
import { ImgWrapper } from "./style";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import Radio, { radioClasses } from "@mui/joy/Radio";
import RadioGroup from "@mui/joy/RadioGroup";
import Person from "@mui/icons-material/Person";
import Apartment from "@mui/icons-material/Apartment";
import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";
import TranslateComponent from "./translate";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Sheet from "@mui/joy/Sheet";

import SvgIcon from "@mui/joy/SvgIcon";
import { Checkbox, styled } from "@mui/joy";
import { useTranslation } from "react-i18next";
import Modal from "../ui/Modal";

const API_BASE_URL = "https://api-k.urconnection.co.kr";

interface Country {
  id: number;
  name: string;
}

interface SignUpPayload {
  name: string;
  phone: string;
  position: string;
  place_of_study: string;
  workplace: string;
  gender: string;
  birthday: string;
  visa: string;
  address: string;
  language: string;
  country: string;
  reason: string;
  reason_message: string;
  resume?: string;
  source_id?: string;
}

const VisuallyHiddenInput = styled("input")`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

function ColorSchemeToggle(props: IconButtonProps) {
  const { onClick, ...other } = props;
  const { mode, setMode } = useColorScheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <IconButton
      aria-label="toggle light/dark mode"
      size="sm"
      variant="outlined"
      disabled={!mounted}
      onClick={(event) => {
        setMode(mode === "light" ? "dark" : "light");
        onClick?.(event);
      }}
      {...other}
    >
      {mode === "light" ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}
    </IconButton>
  );
}

interface FormValues {
  name: string;
  phone: string;
  country: string;
  position: string;
  place_of_study: string;
  workplace: string;
  gender: string;
  birthday: string;
  visa: string;
  address: string;
  language: string;
  resume: File | null;
  resumeId: string;
}

function DataComponent() {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const closeModal = () => setIsModalOpen(!isModalOpen);
  const [currentLang, setCurrentLang] = useState("en");
  const [isSent, setIsSent] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [countries, setCountries] = useState<string[]>([]);
  const [visaTypes, setVisaTypes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { mode } = useColorScheme();
  const [otp, setOtp] = useState<string>("");
  const [otp_id, setOtpID] = useState<string>("");
  const [verifyNumber, setVerifyNumber] = useState<boolean>(false);
  const [otpVisible, setOtpVisible] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [checkPrivacy, setCheckPrivacy] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<FormValues>({
    name: "",
    phone: "",
    country: "",
    position: "",
    place_of_study: "",
    workplace: "",
    gender: "",
    birthday: "",
    visa: "",
    address: "",
    resume: null,
    resumeId: "",
    language: "English",
  });
  const [source, setSource] = useState<string | null>(null);

  const [selectedOption, setSelectedOption] = useState("");
  const [reasonType, setReasonType] = useState("");
  const [reasonMessage, setReasonMessage] = useState("");
  const [t, i18n] = useTranslation("global");

  const handleChangeLanguage = (lang: string, label: string) => {
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
    setFormValues((prev) => ({ ...prev, language: label }));
  };

  const handleCountryChange = (
    event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | null,
    value: string | null
  ) => {
    if (value) {
      setFormValues((prev) => ({
        ...prev,
        country: value,
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;
    if (name === "resume" && files && files[0]) {
      const file = files[0];
      setFormValues((prev) => ({ ...prev, resume: file }));

      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      setIsUploading(true);
      axios
        .post(`${API_BASE_URL}/upload/file`, uploadFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          if (response.data.success && response.data.file_id) {
            setFormValues((prev) => ({
              ...prev,
              resumeId: response.data.file_id,
            }));
            toast.success(t("errors.resumeUploadSuccess"));
          }
        })
        .catch((error) => {
          toast.error(
            error.response?.data?.error?.msg || t("errors.resumeUploadFailed")
          );
          setFormValues((prev) => ({ ...prev, resume: null }));
        })
        .finally(() => {
          setIsUploading(false);
        });
    } else {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (
    event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | null,
    value: string | null
  ) => {
    if (value) {
      setFormValues((prev) => ({ ...prev, visa: value }));
    }
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedOption(value);
    if (value === "Other") {
      setFormValues((prev) => ({
        ...prev,
        workplace: "",
        place_of_study: "",
        position: "Other",
      }));
    }
    if (value === "Employee") {
      setFormValues((prev) => ({
        ...prev,
        place_of_study: "",
        position: "Employee",
      }));
    }
    if (value === "Student") {
      setFormValues((prev) => ({
        ...prev,
        workplace: "",
        position: "Student",
      }));
    }
  };

  const handleReasonTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setReasonType(event.target.value);
  };

  const handleReasonMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setReasonMessage(event.target.value);
  };

  useEffect(() => {
    if (active) {
      setReasonType("");
      setReasonMessage("");
    }
  }, [reasonType, reasonMessage, active]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkPrivacy) {
      toast.error(t("errors.privacyRequired"));
      return;
    }

    if (!verifyNumber) {
      toast.error(t("errors.phoneNotVerified"));
      return;
    }

    const payload: SignUpPayload = {
      name: formValues.name,
      phone: formValues.phone,
      position: formValues.position,
      place_of_study: formValues.place_of_study,
      workplace: formValues.workplace,
      gender: formValues.gender,
      birthday: formValues.birthday,
      visa: formValues.visa,
      address: formValues.address,
      language: formValues.language,
      country: formValues.country,
      reason: reasonType,
      reason_message: reasonMessage,
    };

    if (formValues.resumeId) {
      payload.resume = formValues.resumeId;
    }

    if (source && source?.length === 24) {
      payload.source_id = source;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/user-data/sign-up`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.success) {
        toast.success(t("errors.submitSuccess"));
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
        }, 3000);
        setActive(true);
        setCheckPrivacy(false);
        setVerifyNumber(false);
        setFormValues({
          name: "",
          phone: "",
          country: "",
          position: "",
          place_of_study: "",
          workplace: "",
          gender: "",
          birthday: "",
          visa: "",
          address: "",
          resume: null,
          resumeId: "",
          language: "English",
        });
        setSelectedOption("");
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        toast.error(
          error.response.data?.error?.msg || t("errors.submitFailed")
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const countryResponse = await axios.get(
          `${API_BASE_URL}/info/all-country`
        );
        if (countryResponse.data.success && countryResponse.data.data) {
          const countryNames = countryResponse.data.data.map(
            (country: Country) => country.name
          );
          setCountries(countryNames);
        }
      } catch (error) {
        toast.error(t("errors.loadCountriesFailed"));
      }

      try {
        const visaResponse = await axios.get(`${API_BASE_URL}/info/visa-types`);
        if (visaResponse.data.success && visaResponse.data.data) {
          setVisaTypes(visaResponse.data.data);
        }
      } catch (error) {
        toast.error(t("errors.loadVisaTypesFailed"));
      }

      const urlParams = new URLSearchParams(window.location.search);
      const sourceParam = urlParams.get("source");
      setSource(sourceParam);
      if (sourceParam && sourceParam.length === 24) {
        try {
          await axios.get(`${API_BASE_URL}/sns-link/open/${sourceParam}`, {
            withCredentials: true,
          });
        } catch (error) {}
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            setIsSent(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [remainingTime]);

  const sendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSent(true);
      setRemainingTime(75);
      const response = await axios.post(`${API_BASE_URL}/user-data/send-otp`, {
        phone: formValues.phone,
      });
      if (response?.data?.success) {
        setOtpVisible(true);
        toast.success(response.data.data.remarks);
        setOtpID(response.data.data.verification_id);
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        toast.error(error.response.data?.error?.msg || "Failed to send OTP");
      }
      setIsSent(false);
      setRemainingTime(0);
    }
  };

  const handleVerifyNumber = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API_BASE_URL}/user-data/verify-otp`,
        {
          phone: formValues.phone,
          verification_id: otp_id,
          verification_code: otp,
        }
      );
      if (response?.data?.success) {
        setVerifyNumber(true);
        setOtpVisible(false);
        setOtp("");
        setOtpID("");
        toast.success(response?.data?.msg);
      } else {
        toast.error(response.data?.msg);
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data) {
        toast.error(
          error.response.data?.error?.msg || t("errors.verifyOtpFailed")
        );
      }
    }
  };

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <Box sx={{ px: 2, py: 1 }}>
          <h1
            style={{
              textAlign: "center",
              margin: "5px 0px 25px",
              fontSize: "26px",
            }}
          >
            {t("other.chooseLang")}:{" "}
          </h1>
          <TranslateComponent
            value={currentLang}
            handleChangeLanguage={handleChangeLanguage}
          />
        </Box>
      </Modal>

      <Modal
        isOpen={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          setFormValues({
            name: "",
            phone: "",
            country: "",
            position: "",
            place_of_study: "",
            workplace: "",
            gender: "",
            birthday: "",
            visa: "",
            address: "",
            resume: null,
            resumeId: "",
            language: currentLang === "uzb" ? "O'zbek" : "English",
          });
          setSelectedOption("");
          setCheckPrivacy(false);
          setVerifyNumber(process.env.NODE_ENV === "development");
        }}
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography level="h3">{t("success.title")}</Typography>
          <Typography sx={{ mb: 3 }}>{t("success.message")}</Typography>
          <Button
            onClick={() => setSuccessModalOpen(false)}
            fullWidth
            variant="solid"
            color="primary"
          >
            {t("success.close")}
          </Button>
        </Box>
      </Modal>

      <CssVarsProvider defaultMode="light" disableTransitionOnChange>
        <CssBaseline />

        <GlobalStyles
          styles={{
            ":root": {
              "--Form-maxWidth": "800px",
              "--Transition-duration": "0.4s",
            },
          }}
        />
        <Box
          sx={(theme) => ({
            transition: "width var(--Transition-duration)",
            transitionDelay: "calc(var(--Transition-duration) + 0.1s)",
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "flex-end",
            backdropFilter: "blur(12px)",
            backgroundColor: "rgba(255 255 255 / 0.2)",
            [theme.getColorSchemeSelector("dark")]: {
              backgroundColor: "rgba(19 19 24 / 0.4)",
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              width: "100%",
              px: 2,
            }}
          >
            <Box
              component="header"
              sx={{
                py: 3,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ gap: 2, display: "flex", alignItems: "center" }}>
                <ImgWrapper src={mode === "light" ? logo : darkLogo} />
              </Box>

              <ColorSchemeToggle />
            </Box>
            <Box
              component="main"
              sx={{
                my: "auto",
                py: 2,
                pb: 5,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: 400,
                maxWidth: "100%",
                mx: "auto",
                borderRadius: "sm",
                "& form": {
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                },
                [`& .MuiFormLabel-asterisk`]: {
                  visibility: "hidden",
                },
              }}
            >
              <>
                <TranslateComponent
                  handleChangeLanguage={handleChangeLanguage}
                  value={currentLang}
                />
              </>
              <Stack gap={4} sx={{ mb: 2 }}>
                <Stack gap={1}>
                  <Typography
                    component="h1"
                    level="h3"
                    sx={{ textAlign: "center" }}
                  >
                    {t("MainTitle.Title")}
                  </Typography>
                  <Typography level="body-md" sx={{ textAlign: "center" }}>
                    {t("MainTitle.subTitle")}
                    <Link
                      href="https://urconnection.co.kr/"
                      level="title-md"
                      sx={{ marginLeft: 2 }}
                    >
                      {t("homepage")}
                    </Link>
                  </Typography>
                </Stack>
              </Stack>
              <Divider
                sx={(theme) => ({
                  [theme.getColorSchemeSelector("light")]: {
                    color: { xs: "#FFF", md: "text.tertiary" },
                  },
                })}
              />
              <Stack gap={4} sx={{ mt: 2 }}>
                <form onSubmit={handleSubmit}>
                  <FormControl required>
                    <FormLabel>{t("mainInfo.name")}</FormLabel>
                    <Input
                      type="text"
                      name="name"
                      placeholder="Muhammad"
                      value={formValues.name}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                  <FormControl required>
                    <FormLabel>{t("mainInfo.gender")}</FormLabel>
                    <RadioGroup
                      name="gender"
                      value={formValues.gender}
                      onChange={handleInputChange}
                      sx={{ my: 1 }}
                    >
                      <div style={{ display: "flex" }}>
                        <Radio value="female" label={t("gender.female")} />

                        <Radio
                          value="male"
                          label={t("gender.male")}
                          sx={{ marginLeft: 10 }}
                        />
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormControl required>
                    <FormLabel>{t("mainInfo.country")}</FormLabel>
                    <Select
                      placeholder={t("mainInfo.country")}
                      name="country"
                      value={formValues.country || ""}
                      onChange={handleCountryChange}
                      indicator={<KeyboardArrowDown />}
                      sx={{
                        [`& .${selectClasses.indicator}`]: {
                          transition: "0.2s",
                          [`&.${selectClasses.expanded}`]: {
                            transform: "rotate(-180deg)",
                          },
                        },
                      }}
                    >
                      {countries.map((country) => (
                        <Option key={country} value={country}>
                          {country}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl required>
                    <FormLabel>{t("mainInfo.phone")}</FormLabel>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          width:
                            formValues.phone && !verifyNumber ? "65%" : "100%",
                        }}
                      >
                        <Input
                          type="tel"
                          name="phone"
                          placeholder="+8210 2892 3032"
                          value={formValues.phone}
                          disabled={verifyNumber}
                          onFocus={(e) => {
                            if (!e.target.value) {
                              e.target.value = "+82";
                              handleInputChange(e);
                            }
                          }}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        {formValues.phone && !verifyNumber ? (
                          <Button
                            onClick={sendVerification}
                            disabled={isSent}
                            type="button"
                          >
                            {isSent
                              ? `${t("otp.wait")} ${remainingTime}s`
                              : otpVisible
                              ? t("otp.resend")
                              : t("otp.verify")}
                          </Button>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </FormControl>
                  {otpVisible ? (
                    <FormControl required>
                      <FormLabel>{t("otp.verifyLabel")}</FormLabel>
                      <div style={{ display: "flex" }}>
                        <div style={{ width: otp ? "85%" : "100%" }}>
                          <Input
                            type="number"
                            name="otp"
                            placeholder="1234"
                            value={otp}
                            onChange={(e) => {
                              setOtp(e.target.value);
                            }}
                          />
                        </div>
                        <div>
                          {otp ? (
                            <Button
                              onClick={handleVerifyNumber}
                              type="button"
                              sx={{ marginLeft: 2 }}
                            >
                              {t("otp.verify")}
                            </Button>
                          ) : (
                            ""
                          )}
                        </div>
                      </div>
                    </FormControl>
                  ) : (
                    ""
                  )}
                  <FormControl required>
                    <FormLabel>{t("mainInfo.birthday")}</FormLabel>
                    <Input
                      type="date"
                      name="birthday"
                      value={formValues.birthday}
                      onChange={handleInputChange}
                      slotProps={{
                        input: {
                          min: "1950-01-02",
                        },
                      }}
                    />
                  </FormControl>
                  <FormControl required>
                    <FormLabel>{t("mainInfo.Visa")}</FormLabel>
                    <Select
                      placeholder="Select a VISA"
                      name="visa"
                      value={formValues.visa || ""}
                      onChange={handleSelectChange}
                      indicator={<KeyboardArrowDown />}
                      sx={{
                        [`& .${selectClasses.indicator}`]: {
                          transition: "0.2s",
                          [`&.${selectClasses.expanded}`]: {
                            transform: "rotate(-180deg)",
                          },
                        },
                      }}
                    >
                      {visaTypes.map((visa) => (
                        <Option key={visa} value={visa}>
                          {visa}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl required>
                    <FormLabel>{t("mainInfo.Address")}</FormLabel>
                    <Input
                      type="text"
                      name="address"
                      placeholder="서울(Seoul)"
                      value={formValues.address}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  <FormControl required>
                    <FormLabel>{t("container.position")}</FormLabel>
                    <RadioGroup
                      aria-label="Your plan"
                      name="people"
                      value={selectedOption}
                      onChange={handleOptionChange}
                    >
                      <List
                        sx={{
                          minWidth: 240,
                          "--List-gap": "0.5rem",
                          "--ListItem-paddingY": "1rem",
                          "--ListItem-radius": "8px",
                          "--ListItemDecorator-size": "32px",
                        }}
                      >
                        {["Student", "Employee", "Other"].map((item) => (
                          <ListItem
                            variant="outlined"
                            key={item}
                            sx={{ boxShadow: "sm" }}
                          >
                            <ListItemDecorator>
                              {item === "Student" ? <Person /> : <Apartment />}
                            </ListItemDecorator>
                            <Radio
                              overlay
                              value={item}
                              label={item}
                              sx={{ flexGrow: 1, flexDirection: "row-reverse" }}
                              slotProps={{
                                action: ({ checked }) => ({
                                  sx: (theme) => ({
                                    ...(checked && {
                                      inset: -1,
                                      border: "2px solid",
                                      borderColor:
                                        theme.vars.palette.primary[500],
                                    }),
                                  }),
                                }),
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </RadioGroup>
                  </FormControl>

                  {selectedOption === "Student" && (
                    <FormControl required>
                      <FormLabel>{t("container.student")}</FormLabel>
                      <Input
                        type="text"
                        name="place_of_study"
                        placeholder="Enter your university"
                        value={formValues.place_of_study}
                        onChange={handleInputChange}
                      />
                    </FormControl>
                  )}
                  {selectedOption === "Employee" && (
                    <FormControl required>
                      <FormLabel>{t("container.employee")}</FormLabel>
                      <Input
                        type="text"
                        name="workplace"
                        placeholder="Enter your company"
                        value={formValues.workplace}
                        onChange={handleInputChange}
                      />
                    </FormControl>
                  )}
                  <FormLabel>{t("resume.note")}</FormLabel>
                  <Button
                    component="label"
                    role={undefined}
                    tabIndex={-1}
                    variant="outlined"
                    color="neutral"
                    disabled={isUploading}
                    loading={isUploading}
                    startDecorator={
                      <SvgIcon>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                          />
                        </svg>
                      </SvgIcon>
                    }
                  >
                    {isUploading
                      ? "Uploading..."
                      : formValues.resume
                      ? "Uploaded"
                      : "Upload Resume"}
                    <VisuallyHiddenInput
                      type="file"
                      name="resume"
                      onChange={handleInputChange}
                    />
                  </Button>

                  <FormControl required>
                    <FormLabel>{t("reason.main")}</FormLabel>
                    <RadioGroup
                      aria-label="platform"
                      value={reasonType}
                      overlay
                      name="reasonType"
                      sx={{
                        flexDirection: "row",
                        gap: 1,
                        [`& .${radioClasses.checked}`]: {
                          [`& .${radioClasses.action}`]: {
                            inset: -1,
                            border: "3px solid",
                            borderColor: "primary.500",
                          },
                        },
                        [`& .${radioClasses.radio}`]: {
                          display: "contents",
                          "& > svg": {
                            zIndex: 2,
                            position: "absolute",
                            top: "-8px",
                            right: "-8px",
                            bgcolor: "background.surface",
                            borderRadius: "50%",
                          },
                        },
                      }}
                      onChange={handleReasonTypeChange}
                    >
                      {["Education", "Work", "Study", "Visa", "Other"].map(
                        (value) => (
                          <Sheet
                            key={value}
                            variant="outlined"
                            sx={{
                              borderRadius: "md",
                              boxShadow: "sm",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              p: 2,
                              flex: 1,
                            }}
                          >
                            <Radio
                              id={value}
                              value={value}
                              checkedIcon={<CheckCircleRoundedIcon />}
                            />
                            <FormLabel htmlFor={value}>{value}</FormLabel>
                          </Sheet>
                        )
                      )}
                    </RadioGroup>
                  </FormControl>

                  {reasonType && (
                    <FormControl required sx={{ mt: 2 }}>
                      <FormLabel>{t("reason.reason_message")}</FormLabel>
                      <Textarea
                        minRows={3}
                        placeholder={t("reason.reason_message")}
                        value={reasonMessage}
                        onChange={handleReasonMessageChange}
                      />
                    </FormControl>
                  )}

                  <Checkbox
                    onChange={(e) => {
                      setCheckPrivacy(e.target.checked);
                    }}
                    checked={checkPrivacy}
                    label={<Fragment>{t("privacy")}</Fragment>}
                  />

                  <Stack gap={4} sx={{ mt: 2 }}>
                    <Button
                      type="submit"
                      fullWidth
                      disabled={isLoading}
                      loading={isLoading}
                    >
                      {t("sendButton.done")}
                    </Button>
                  </Stack>
                </form>
              </Stack>
            </Box>
          </Box>
        </Box>
      </CssVarsProvider>
    </>
  );
}

export default function App() {
  return (
    <CssVarsProvider defaultMode="light" disableTransitionOnChange>
      <DataComponent />
    </CssVarsProvider>
  );
}
